import { forwardRef, useState, useMemo, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  TextInput,
  Select,
  Menu,
  ActionIcon,
  Checkbox,
  Pagination,
  Text,
  Badge,
  Tooltip,
  Flex,
  Paper,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconSortAscending,
  IconSortDescending,
  IconDots,
  IconEdit,
  IconTrash,
  IconEye,
  IconDownload,
  IconPlus,
  IconRefresh,
  IconColumns,
} from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn, StaggeredList } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface EntityColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (item: T, index: number) => ReactNode;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  visible?: boolean;
}

export interface EntityAction<T> {
  key: string;
  label: string;
  icon?: ReactNode;
  color?: string;
  onClick: (item: T) => void;
  visible?: (item: T) => boolean;
  disabled?: (item: T) => boolean;
}

export interface EntityFilter {
  key: string;
  label: string;
  type: 'select' | 'date' | 'dateRange' | 'text' | 'number';
  options?: { value: string; label: string }[];
}

export interface EntityListProps<T extends { id: string | number }> {
  data: T[];
  columns: EntityColumn<T>[];
  actions?: EntityAction<T>[];
  filters?: EntityFilter[];
  title?: string;
  description?: string;
  loading?: boolean;
  selectable?: boolean;
  selectedIds?: (string | number)[];
  onSelectionChange?: (ids: (string | number)[]) => void;
  onRowClick?: (item: T) => void;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
  };
  sorting?: {
    column: string;
    direction: 'asc' | 'desc';
    onSort: (column: string, direction: 'asc' | 'desc') => void;
  };
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  bulkActions?: {
    label: string;
    icon?: ReactNode;
    onClick: (ids: (string | number)[]) => void;
    color?: string;
  }[];
  onAdd?: () => void;
  onRefresh?: () => void;
  onExport?: () => void;
  emptyState?: ReactNode;
  headerExtra?: ReactNode;
  viewMode?: 'table' | 'grid' | 'list';
  onViewModeChange?: (mode: 'table' | 'grid' | 'list') => void;
  gridItemRender?: (item: T) => ReactNode;
  listItemRender?: (item: T) => ReactNode;
  className?: string;
}

function EntityListInner<T extends { id: string | number }>(
  {
    data,
    columns,
    actions = [],
    filters = [],
    title,
    description,
    loading = false,
    selectable = false,
    selectedIds = [],
    onSelectionChange,
    onRowClick,
    pagination,
    sorting,
    search,
    bulkActions = [],
    onAdd,
    onRefresh,
    onExport,
    emptyState,
    headerExtra,
    viewMode = 'table',
    onViewModeChange,
    gridItemRender,
    listItemRender,
    className,
  }: EntityListProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columns.filter((c) => c.visible !== false).map((c) => String(c.key))
  );
  const [activeFilters, setActiveFilters] = useState<Record<string, unknown>>({});

  const filteredColumns = useMemo(
    () => columns.filter((c) => visibleColumns.includes(String(c.key))),
    [columns, visibleColumns]
  );

  const isAllSelected = data.length > 0 && selectedIds.length === data.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < data.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.(data.map((item) => item.id));
    }
  };

  const handleSelectItem = (id: string | number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange?.(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange?.([...selectedIds, id]);
    }
  };

  const handleSort = (column: string) => {
    if (!sorting) return;
    const newDirection = sorting.column === column && sorting.direction === 'asc' ? 'desc' : 'asc';
    sorting.onSort(column, newDirection);
  };

  const getValue = (item: T, key: string): unknown => {
    const keys = key.split('.');
    let value: unknown = item;
    for (const k of keys) {
      value = (value as Record<string, unknown>)?.[k];
    }
    return value;
  };

  const renderTableView = () => (
    <Box style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            {selectable && (
              <th style={{ padding: '12px 8px', width: 40 }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            {filteredColumns.map((column) => (
              <th
                key={String(column.key)}
                style={{
                  padding: '12px 8px',
                  textAlign: column.align || 'left',
                  width: column.width,
                  cursor: column.sortable ? 'pointer' : 'default',
                }}
                onClick={() => column.sortable && handleSort(String(column.key))}
              >
                <Group gap={4} justify={column.align === 'right' ? 'flex-end' : 'flex-start'}>
                  <Text size="sm" fw={600} style={accessibilityStyles}>
                    {column.label}
                  </Text>
                  {column.sortable && sorting?.column === String(column.key) && (
                    sorting.direction === 'asc' ? (
                      <IconSortAscending size={14} />
                    ) : (
                      <IconSortDescending size={14} />
                    )
                  )}
                </Group>
              </th>
            ))}
            {actions.length > 0 && (
              <th style={{ padding: '12px 8px', width: 60, textAlign: 'center' }}>
                <Text size="sm" fw={600} style={accessibilityStyles}>Actions</Text>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={item.id}
              style={{
                borderBottom: '1px solid var(--mantine-color-gray-2)',
                cursor: onRowClick ? 'pointer' : 'default',
                backgroundColor: selectedIds.includes(item.id)
                  ? 'var(--mantine-color-blue-0)'
                  : 'transparent',
              }}
              onClick={() => onRowClick?.(item)}
            >
              {selectable && (
                <td style={{ padding: '12px 8px' }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.includes(item.id)}
                    onChange={() => handleSelectItem(item.id)}
                  />
                </td>
              )}
              {filteredColumns.map((column) => (
                <td
                  key={String(column.key)}
                  style={{
                    padding: '12px 8px',
                    textAlign: column.align || 'left',
                  }}
                >
                  {column.render
                    ? column.render(item, index)
                    : String(getValue(item, String(column.key)) ?? '-')}
                </td>
              ))}
              {actions.length > 0 && (
                <td style={{ padding: '12px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {actions
                        .filter((action) => !action.visible || action.visible(item))
                        .map((action) => (
                          <Menu.Item
                            key={action.key}
                            leftSection={action.icon}
                            color={action.color}
                            disabled={action.disabled?.(item)}
                            onClick={() => action.onClick(item)}
                          >
                            {action.label}
                          </Menu.Item>
                        ))}
                    </Menu.Dropdown>
                  </Menu>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );

  const renderGridView = () => (
    <StaggeredList staggerDelay={50}>
      <Flex gap="md" wrap="wrap">
        {data.map((item) => (
          <Box key={item.id} style={{ flex: '1 1 300px', maxWidth: 400 }}>
            {gridItemRender ? gridItemRender(item) : (
              <Card
                hoverable
                onClick={() => onRowClick?.(item)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                <Text fw={500} style={accessibilityStyles}>{String(getValue(item, String(columns[0]?.key)))}</Text>
                {columns.slice(1, 3).map((col) => (
                  <Text key={String(col.key)} size="sm" c="dimmed" style={accessibilityStyles}>
                    {col.label}: {String(getValue(item, String(col.key)) ?? '-')}
                  </Text>
                ))}
              </Card>
            )}
          </Box>
        ))}
      </Flex>
    </StaggeredList>
  );

  const renderListView = () => (
    <StaggeredList staggerDelay={30}>
      <Stack gap="xs">
        {data.map((item) => (
          <Box key={item.id}>
            {listItemRender ? listItemRender(item) : (
              <Paper
                p="md"
                withBorder
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                onClick={() => onRowClick?.(item)}
              >
                <Group justify="space-between">
                  <Box>
                    <Text fw={500} style={accessibilityStyles}>{String(getValue(item, String(columns[0]?.key)))}</Text>
                    <Text size="sm" c="dimmed" style={accessibilityStyles}>
                      {columns.slice(1, 3).map((col) => `${col.label}: ${getValue(item, String(col.key)) ?? '-'}`).join(' • ')}
                    </Text>
                  </Box>
                  {actions.length > 0 && (
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {actions.map((action) => (
                          <Menu.Item
                            key={action.key}
                            leftSection={action.icon}
                            color={action.color}
                            onClick={() => action.onClick(item)}
                          >
                            {action.label}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Paper>
            )}
          </Box>
        ))}
      </Stack>
    </StaggeredList>
  );

  return (
    <FadeIn>
      <Card ref={ref} className={className} p={0}>
        {/* Header */}
        {(title || onAdd || onRefresh) && (
          <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Group justify="space-between">
              <Box>
                {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
                {description && <Text size="sm" c="dimmed" style={accessibilityStyles}>{description}</Text>}
              </Box>
              <Group gap="xs">
                {headerExtra}
                {onRefresh && (
                  <Tooltip label="Refresh">
                    <ActionIcon variant="subtle" onClick={onRefresh}>
                      <IconRefresh size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {onExport && (
                  <Tooltip label="Export">
                    <ActionIcon variant="subtle" onClick={onExport}>
                      <IconDownload size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {onAdd && (
                  <Button leftSection={<IconPlus size={16} />} onClick={onAdd}>
                    Add New
                  </Button>
                )}
              </Group>
            </Group>
          </Box>
        )}

        {/* Toolbar */}
        <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
          <Group justify="space-between">
            <Group gap="sm">
              {search && (
                <TextInput
                  placeholder={search.placeholder || 'Search...'}
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                  leftSection={<IconSearch size={16} />}
                  style={{ width: 250 }}
                />
              )}
              {filters.length > 0 && (
                <Menu position="bottom-start">
                  <Menu.Target>
                    <Button variant="subtle" leftSection={<IconFilter size={16} />}>
                      Filters
                      {Object.keys(activeFilters).length > 0 && (
                        <Badge size="xs" ml={4}>{Object.keys(activeFilters).length}</Badge>
                      )}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown p="md" style={{ minWidth: 250 }}>
                    <Stack gap="sm">
                      {filters.map((filter) => (
                        <Box key={filter.key}>
                          {filter.type === 'select' && filter.options && (
                            <Select
                              label={filter.label}
                              data={filter.options}
                              value={activeFilters[filter.key] as string}
                              onChange={(value) => setActiveFilters({ ...activeFilters, [filter.key]: value })}
                              clearable
                            />
                          )}
                          {filter.type === 'text' && (
                            <TextInput
                              label={filter.label}
                              value={(activeFilters[filter.key] as string) || ''}
                              onChange={(e) => setActiveFilters({ ...activeFilters, [filter.key]: e.target.value })}
                            />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
            <Group gap="sm">
              {selectedIds.length > 0 && bulkActions.length > 0 && (
                <Group gap="xs">
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>{selectedIds.length} selected</Text>
                  {bulkActions.map((action) => (
                    <Button
                      key={action.label}
                      variant="subtle"
                      size="xs"
                      color={action.color}
                      leftSection={action.icon}
                      onClick={() => action.onClick(selectedIds)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Group>
              )}
              <Menu position="bottom-end">
                <Menu.Target>
                  <Tooltip label="Columns">
                    <ActionIcon variant="subtle">
                      <IconColumns size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  {columns.map((column) => (
                    <Menu.Item key={String(column.key)} closeMenuOnClick={false}>
                      <Checkbox
                        label={column.label}
                        checked={visibleColumns.includes(String(column.key))}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setVisibleColumns([...visibleColumns, String(column.key)]);
                          } else {
                            setVisibleColumns(visibleColumns.filter((c) => c !== String(column.key)));
                          }
                        }}
                      />
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Box>

        {/* Content */}
        <Box p="md">
          {data.length === 0 ? (
            emptyState || (
              <Box py="xl" ta="center">
                <Text c="dimmed" style={accessibilityStyles}>No data available</Text>
              </Box>
            )
          ) : (
            <>
              {viewMode === 'table' && renderTableView()}
              {viewMode === 'grid' && renderGridView()}
              {viewMode === 'list' && renderListView()}
            </>
          )}
        </Box>

        {/* Pagination */}
        {pagination && pagination.total > pagination.pageSize && (
          <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Group justify="space-between">
              <Text size="sm" c="dimmed" style={accessibilityStyles}>
                Showing {(pagination.page - 1) * pagination.pageSize + 1} to{' '}
                {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
              </Text>
              <Pagination
                value={pagination.page}
                onChange={pagination.onPageChange}
                total={Math.ceil(pagination.total / pagination.pageSize)}
              />
            </Group>
          </Box>
        )}
      </Card>
    </FadeIn>
  );
}

export const EntityList = forwardRef(EntityListInner) as <T extends { id: string | number }>(
  props: EntityListProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement;

export default EntityList;
